// Answers "does the chosen transcriber and voice fit the language setting?" from the form values.
// The Language banner, the accordion marks, the Agent tab mark and the save all use this file.
import ttsProviders from "@/config/tts-providers.json";
import sttProviders from "@/config/stt-providers.json";
import {
  checkLanguageConfiguration,
  deriveSttLanguage,
  deriveTtsLanguage,
  validateLanguageSetup,
  type LanguageConfiguration,
  type LanguageConflict,
  type VoiceLike,
} from "@/config/language-rules";
import { isRealtimeModel } from "@/utils/realtimeModels";
import { useFormContext } from "react-hook-form";

type FormValues = Record<string, any>;

/** The setting that applies to these form values: null when unset, junk, or the section is hidden. */
export function activeLanguageSetting(values: FormValues): LanguageConfiguration | null {
  // The feature is cascade only; the other tabs use the same helper to switch it off.
  if (!values || isRealtimeModel(values.llm_provider, values.llm_model)) return null;
  return checkLanguageConfiguration(values.language_configuration);
}

/**
 * True when the setting that applies is English alone (single mode, `en`): the only setting the
 * ConvFill filler runs on in voxi-2-0. No setting, any other one, or a realtime model is false.
 */
export function isEnglishOnly(values: FormValues): boolean {
  const setting = activeLanguageSetting(values);
  return setting?.mode === "single" && setting.languages.length === 1 && setting.languages[0] === "en";
}

/** The voice object from the config, or undefined for a custom voice id. */
function findVoice(provider: string | undefined, voiceId: string | undefined): VoiceLike | undefined {
  if (!provider || !voiceId) return undefined;
  const voices = (ttsProviders as any).providers?.[provider]?.voices;
  if (!Array.isArray(voices)) return undefined;
  return voices.find((v: VoiceLike) => v.id === voiceId);
}

export function getLanguageConflicts(values: FormValues): LanguageConflict[] {
  const setting = activeLanguageSetting(values);
  if (!setting) return [];
  const sttProvider: string | undefined = values.stt_provider;
  const ttsProvider: string | undefined = values.tts_provider;
  return validateLanguageSetup(setting, {
    sttProvider,
    sttModel: values.stt_model,
    ttsProvider,
    voice: findVoice(ttsProvider, values.voice),
    sttProviderName: sttProvider ? (sttProviders as any).providers?.[sttProvider]?.name : undefined,
    ttsProviderName: ttsProvider ? (ttsProviders as any).providers?.[ttsProvider]?.name : undefined,
  });
}

/** Provider language values the setting implies. A key is undefined when the form's own field stays. */
export function getDerivedLanguages(values: FormValues): { stt?: string; tts?: string } {
  const setting = activeLanguageSetting(values);
  if (!setting) return {};
  return {
    stt: values.stt_provider ? deriveSttLanguage(setting, values.stt_provider) : undefined,
    tts: values.tts_provider ? deriveTtsLanguage(setting, values.tts_provider) : undefined,
  };
}

/** Live conflicts for the form in context. */
export function useLanguageConflicts(): LanguageConflict[] {
  const { watch } = useFormContext();
  return getLanguageConflicts({
    llm_provider: watch("llm_provider"),
    llm_model: watch("llm_model"),
    language_configuration: watch("language_configuration"),
    stt_provider: watch("stt_provider"),
    stt_model: watch("stt_model"),
    tts_provider: watch("tts_provider"),
    voice: watch("voice"),
  });
}
